import React, { useCallback, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FieldArray, ArrayHelpers, Field, FastField } from 'formik';

import Checkbox from '@material-ui/core/Checkbox';
import FormGroup from '@material-ui/core/FormGroup';
import FormControl, { FormControlProps } from '@material-ui/core/FormControl';
import FormLabel from '@material-ui/core/FormLabel';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormHelperText from '@material-ui/core/FormHelperText';
import { Theme } from '@material-ui/core/styles/createTheme';
import makeStyles from '@material-ui/core/styles/makeStyles';
import createStyles from '@material-ui/core/styles/createStyles';

import { getErrorMessageKey } from 'helpers/helpers';
import Info from 'components/Tooltip/Info';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    formGroup: (props: any) => ({
      marginBottom: props.noMargin ? undefined : theme.spacing(4),
      '&:last-child:not(:only-child)': {
        marginBottom: 0,
      },
      '& .MuiFormGroup-root': {
        flexDirection: props.horizontal ? 'row' as 'row' : 'column' as 'column',
      },
    }),
    info: {
      //padding: theme.spacing(2, 0),
      fontSize: '14px',
      fontWeight: 400,
      color: '#58585a',
    },
    font: {
      '& .MuiFormLabel-root, & .MuiTypography-root': { fontSize: '14px' },
      '& .MuiSvgIcon-root': { fontSize: '22px' },
    },
    light: {
      '& .MuiFormLabel-root, & .MuiTypography-root': { color: '#58585a' },
    }
  }),
);

interface OwnProps {
  fast?: boolean;
  fullWidth?: boolean;
  name: string;
  values: string[];
  options: string[];
  labels?: string[];
  label: string;
  className?: string;
  errorMessage?: string;
  notaIndex?: string;
  optPrefix?: string;
  optSuffix?: string;
  afterChange?: () => void;
  afterChangeItem?: (value: string, checked: boolean) => void;
  noMargin?: boolean;
  literal?: boolean;
  horizontal?: boolean;
  tooltip?: string;
  info?: string;
  disabled?: boolean;
}

type Props = OwnProps & FormControlProps;

type PushRemove = Pick<ArrayHelpers, 'push' | 'remove'>;

/**
 * Basic wrapper around Group of checkboxes
 * @param name {string} - id and name for the input and name of formik value
 * @param formikProps {any} - formik props so Formik can do it's things
 * @param label {string}
 * @param options {string[]} - labels of options
 */
const FormCheckboxGroup: React.FC<Props> = ({
  fast = true,
  options,
  labels,
  label,
  name,
  fullWidth,
  className,
  values,
  errorMessage, // errorMessage, because error is already on FormControlProps
  notaIndex,
  optPrefix,
  optSuffix,
  afterChange,
  afterChangeItem,
  noMargin,
  literal,
  horizontal,
  tooltip,
  info,
  disabled
}) => {
  const { t } = useTranslation();
  let notaChecked: boolean = !!notaIndex && values?.indexOf(notaIndex) > -1;

  const [callAfterChange, setCallAfterChange] = useState(false);

  useEffect(() => {
    if (callAfterChange) {
      afterChange?.();
      setCallAfterChange(false);
    }
  }, [callAfterChange, afterChange]);

  const handleCheck = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>, arrayHelpers: PushRemove) => {
      const { value: newValue } = e.target;

      if (values !== undefined && e.target.checked && newValue === notaIndex) {
        for (let i = 0; i < values.length; i++) {
          arrayHelpers.remove(0);
        }
      }

      if (afterChangeItem) {
        afterChangeItem(newValue, e.target.checked);
      }

      e.target.checked ? arrayHelpers.push(newValue) : arrayHelpers.remove(values.indexOf(newValue));

      if (afterChange) {
        setCallAfterChange(true);
      }
    },
    [values, notaIndex, afterChange, afterChangeItem],
  );
  const classes = useStyles({ noMargin, horizontal });

  return (
    <FormControl
      component={'fieldset' as 'div'}
      fullWidth={fullWidth || true}
      className={`${classes.formGroup} ${classes.font} ${className}`}
      error={!!errorMessage && errorMessage.length > 0}
    >
      <div>
      <FormLabel component={'legend' as any} style={{ marginBottom: '8px' }}>
        <strong>{label}</strong>
        {tooltip && <Info content={<>{tooltip}</>} inline />}
        {info && <p className={classes.info}>{info}</p>}
      </FormLabel>
      <FieldArray
        name={name}
        render={({ push, remove }) => (
          <FormGroup>
            {options.map((option, i) => (
              <ControlLabel
                fast={fast}
                key={option}
                option={option}
                label={labels ? labels[i] : undefined}
                i={i}
                name={name}
                value={values}
                handleCheck={handleCheck}
                push={push}
                remove={remove}
                disabled={notaChecked && notaIndex !== `${i + 1}`}
                groupDisabled={disabled}
                optPrefix={optPrefix}
                optSuffix={optSuffix}
                literal={literal}
              />
            ))}
          </FormGroup>
        )}
      />
      {errorMessage && errorMessage.length > 0 && (
        <FormHelperText style={{ color: 'red' }}>{t(getErrorMessageKey(errorMessage, false))}</FormHelperText>
      )}
      </div>
    </FormControl>
  );
};

interface ControlLabelProps extends PushRemove {
  fast?: boolean;
  option: string;
  label?: string;
  name: string;
  value: string[];
  handleCheck: (e: React.ChangeEvent<HTMLInputElement>, arrayHelpers: PushRemove) => void;
  i: number;
  disabled: boolean;
  optPrefix?: string;
  optSuffix?: string;
  literal?: boolean;
  groupDisabled?: boolean;
}

const ControlLabel: React.FC<ControlLabelProps> = React.memo((props) => {
  const { t } = useTranslation();
  const { option, value, handleCheck, i, push, remove, disabled, groupDisabled, optPrefix, optSuffix, literal, label, fast = true } = props;
  const checked = !!(!disabled && value && value.includes(optPrefix || literal ? option : `${i + 1}`));

  return (
    <FormControlLabel
      label={label ? label : optPrefix || optSuffix ? t(`${optPrefix || ''}${option}${optSuffix || ''}`) : option}
      className={`${props.name}_${optPrefix || literal ? option : i + 1}`}
      control={
        fast ? (
          <FastField
            checked={checked}
            onChange={(e: any) => handleCheck(e, { push, remove })}
            value={optPrefix || literal ? option : `${i + 1}`}
            component={Checkbox}
            disabled={groupDisabled || disabled}
          />
        ) : (
          <Field
            checked={checked}
            onChange={(e: any) => handleCheck(e, { push, remove })}
            value={optPrefix || literal ? option : `${i + 1}`}
            component={Checkbox}
            disabled={groupDisabled || disabled}
          />
        )
      }
    />
  );
});

export default FormCheckboxGroup;
